import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import bekoDryer from '@/assets/beko-b5t-dryer.webp';
import { needsImageUpgrade } from '@/lib/applianceImageQuality';

interface Appliance {
  id: string;
  name: string;
  modelNumber: string;
  category: string;
  imageUrl?: string;
}

// Local fallback images for known models where web search fails
const localFallbackImages: Record<string, string> = {
  'beko-dryer': bekoDryer,
};

const getLocalFallback = (appliance: Appliance): string | null => {
  const brand = appliance.name.toLowerCase().split(' ')[0];
  const category = appliance.category.toLowerCase();
  const key = `${brand}-${category}`;
  return localFallbackImages[key] || null;
};

const extractBrand = (name: string): string | null => {
  const knownBrands = ['bosch', 'miele', 'aeg', 'siemens', 'electrolux', 'samsung', 'lg', 'whirlpool', 'beko', 'bauknecht'];
  const nameLower = name.toLowerCase();
  for (const brand of knownBrands) {
    if (nameLower.includes(brand)) {
      return brand.charAt(0).toUpperCase() + brand.slice(1);
    }
  }
  return null;
};

const searchForProductShot = async (
  appliance: Appliance,
  onUpdate: (applianceId: string, imageUrl: string) => void,
): Promise<void> => {
  try {
    const brand = extractBrand(appliance.name);
    if (!appliance.modelNumber?.trim() && !brand?.trim()) return;
    const productType = appliance.category.replace('-', ' ');

    const { data, error } = await supabase.functions.invoke('product-image-search', {
      body: {
        modelNumber: appliance.modelNumber,
        brand,
        productType,
      },
    });

    if (!error && data?.success && data?.data?.url) {
      const url: string = data.data.url;
      // Only accept results that themselves pass the strict quality bar
      if (!needsImageUpgrade(url)) {
        onUpdate(appliance.id, url);
        return;
      }
    }

    if (error) console.error('Product image search error:', error);

    const fallback = getLocalFallback(appliance);
    if (fallback) onUpdate(appliance.id, fallback);
  } catch (e) {
    console.error('Failed to search for product shot:', e);
    const fallback = getLocalFallback(appliance);
    if (fallback) onUpdate(appliance.id, fallback);
  }
};

// Process-wide guard so we don't loop on the same id within a session.
const upgradedIds = new Set<string>();

/**
 * Upgrades any appliance image that fails the strict ≥75%-coverage rule.
 * - `onApplianceUpdate` is always called (in-memory state update).
 * - `persist` is optionally called so callers can also write to localStorage / DB.
 */
export const useProductImageUpgrade = (
  appliances: Appliance[],
  onApplianceUpdate: (applianceId: string, imageUrl: string) => void,
  persist?: (applianceId: string, imageUrl: string) => void | Promise<void>,
) => {
  const updateRef = useRef(onApplianceUpdate);
  const persistRef = useRef(persist);
  updateRef.current = onApplianceUpdate;
  persistRef.current = persist;

  const upgradeImages = useCallback(async () => {
    const needsUpgrade = appliances.filter((a) => {
      if (upgradedIds.has(a.id)) return false;
      if (!a.modelNumber?.trim() && !extractBrand(a.name)?.trim()) return false;
      return needsImageUpgrade(a.imageUrl);
    });

    if (needsUpgrade.length === 0) return;

    needsUpgrade.forEach((a) => upgradedIds.add(a.id));

    const handleUpdate = (id: string, url: string) => {
      updateRef.current?.(id, url);
      void persistRef.current?.(id, url);
    };

    const MAX_CONCURRENT = 2;
    for (let i = 0; i < needsUpgrade.length; i += MAX_CONCURRENT) {
      const batch = needsUpgrade.slice(i, i + MAX_CONCURRENT);
      await Promise.all(batch.map((a) => searchForProductShot(a, handleUpdate)));
    }
  }, [appliances]);

  useEffect(() => {
    const t = setTimeout(() => {
      upgradeImages();
    }, 800);
    return () => clearTimeout(t);
  }, [upgradeImages]);
};
